import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

import AuthContext from '../../context/authContext';
import useEmployeeFreeDrinks from '../../hooks/useEmployeeFreeDrinks';
import useBranch from '../../hooks/useBranch';

const STATS_RIGHT = 'employeeFreeDrinks:statistics:read';

const defaultFrom = () => dayjs().subtract(29, 'day').format('YYYY-MM-DD');
const defaultTo = () => dayjs().format('YYYY-MM-DD');

// Statistics view for employee free drinks (Freigetränke), gated by a special right.
// Shows aggregated KPIs, charts and tables and offers CSV/Excel download.
const FreeDrinksStatistics = () => {
  const authCtx = useContext(AuthContext);
  const hasRight = authCtx.checkRight(STATS_RIGHT);

  const { getStatistics, downloadExport } = useEmployeeFreeDrinks();
  const { getBranches } = useBranch();

  const fromRef = useRef(null);
  const toRef = useRef(null);

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [appliedFilter, setAppliedFilter] = useState(() => ({
    from: defaultFrom(),
    to: defaultTo(),
    branchId: '',
  }));
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    if (!hasRight) return;
    const loadBranches = async () => {
      try {
        const result = await getBranches();
        setBranches((result || []).map(b => ({ id: b._id, label: b.locationName })));
      } catch (error) {
        toast.error('Fehler beim Laden der Standorte.');
      }
    };
    loadBranches();
  }, [hasRight, getBranches]);

  useEffect(() => {
    if (!hasRight) return;
    let active = true;
    const loadStats = async () => {
      setIsLoading(true);
      try {
        const result = await getStatistics(appliedFilter);
        if (active) setStats(result);
      } catch (error) {
        if (active) toast.error('Fehler beim Laden der Statistik.');
      } finally {
        if (active) setIsLoading(false);
      }
    };
    loadStats();
    return () => {
      active = false;
    };
  }, [hasRight, getStatistics, appliedFilter]);

  const handleApply = useCallback(() => {
    const from = fromRef.current?.value || defaultFrom();
    const to = toRef.current?.value || defaultTo();
    if (dayjs(from).isAfter(dayjs(to))) {
      toast.error('Das Startdatum darf nicht nach dem Enddatum liegen.');
      return;
    }
    setAppliedFilter({ from, to, branchId });
  }, [branchId]);

  const handleDownload = useCallback(
    async format => {
      setDownloading(format);
      try {
        const blob = await downloadExport(format, appliedFilter);
        if (!blob) return;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `freigetraenke-statistik-${appliedFilter.from}_${appliedFilter.to}.${
          format === 'xlsx' ? 'xlsx' : 'csv'
        }`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        toast.error('Fehler beim Herunterladen der Datei.');
      } finally {
        setDownloading(null);
      }
    },
    [downloadExport, appliedFilter]
  );

  const chartData = useMemo(
    () => (stats?.byDrink || []).map(drink => ({ name: drink.label, Anzahl: drink.count })),
    [stats]
  );

  const drinkColumns = stats?.configDrinks || [];
  const groupColumns = stats?.configGroups || [];

  if (!hasRight) {
    return <Navigate to='/welcome' replace />;
  }

  const kpiCards = stats
    ? [
        { label: 'Freigetränke gesamt', value: stats.total },
        { label: 'Mitarbeiter', value: stats.employeeCount },
        ...(stats.byGroup || []).map(group => ({ label: group.label, value: group.count })),
      ]
    : [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant='h5' component='h1' sx={{ mb: 2 }}>
        Freigetränke-Statistik
      </Typography>

      {/* Filter bar */}
      <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-end' }}>
          <TextField
            label='Von'
            type='date'
            inputRef={fromRef}
            defaultValue={appliedFilter.from}
            InputLabelProps={{ shrink: true }}
            size='small'
          />
          <TextField
            label='Bis'
            type='date'
            inputRef={toRef}
            defaultValue={appliedFilter.to}
            InputLabelProps={{ shrink: true }}
            size='small'
          />
          <FormControl size='small' sx={{ minWidth: 200 }}>
            <InputLabel id='free-drink-stats-branch'>Standort</InputLabel>
            <Select
              labelId='free-drink-stats-branch'
              label='Standort'
              value={branchId}
              onChange={event => setBranchId(event.target.value)}
            >
              <MenuItem value=''>Alle Standorte</MenuItem>
              {branches.map(branch => (
                <MenuItem key={branch.id} value={branch.id}>
                  {branch.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant='contained' onClick={handleApply}>
            Anwenden
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction='row' spacing={1}>
            <Button
              variant='outlined'
              startIcon={
                downloading === 'csv' ? <CircularProgress size={16} /> : <DescriptionIcon />
              }
              disabled={downloading !== null}
              onClick={() => handleDownload('csv')}
            >
              CSV
            </Button>
            <Button
              variant='outlined'
              startIcon={
                downloading === 'xlsx' ? <CircularProgress size={16} /> : <TableChartIcon />
              }
              disabled={downloading !== null}
              onClick={() => handleDownload('xlsx')}
            >
              Excel
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {isLoading && !stats ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* KPI cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {kpiCards.map(card => (
              <Grid item xs={6} sm={4} md={3} key={card.label}>
                <Paper variant='outlined' sx={{ p: 2, height: '100%' }}>
                  <Typography variant='caption' color='text.secondary'>
                    {card.label}
                  </Typography>
                  <Typography variant='h5'>{card.value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Drinks bar chart */}
          <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Freigetränke je Getränk
            </Typography>
            <Box sx={{ width: '100%', height: 280 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='name' />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey='Anzahl' fill='#1976d2' />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>

          {/* Per employee table */}
          <Paper variant='outlined' sx={{ p: 2, mb: 3 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Je Mitarbeiter
            </Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Mitarbeiter</TableCell>
                    <TableCell align='right'>ID</TableCell>
                    <TableCell align='right'>Gesamt</TableCell>
                    {drinkColumns.map(drink => (
                      <TableCell key={drink.key} align='right'>
                        {drink.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats?.byEmployee || []).map(employee => (
                    <TableRow key={employee.userId}>
                      <TableCell>{employee.userName || '—'}</TableCell>
                      <TableCell align='right'>{employee.employeeId ?? '—'}</TableCell>
                      <TableCell align='right'>{employee.total}</TableCell>
                      {drinkColumns.map(drink => (
                        <TableCell key={drink.key} align='right'>
                          {employee.drinks?.[drink.key] || 0}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {(stats?.byEmployee || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3 + drinkColumns.length} align='center'>
                        Keine Daten im gewählten Zeitraum.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Per branch table */}
          <Paper variant='outlined' sx={{ p: 2 }}>
            <Typography variant='subtitle1' sx={{ mb: 1, fontWeight: 600 }}>
              Je Standort
            </Typography>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Standort</TableCell>
                    <TableCell align='right'>Gesamt</TableCell>
                    {groupColumns.map(group => (
                      <TableCell key={group.key} align='right'>
                        {group.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(stats?.byBranch || []).map(branch => (
                    <TableRow key={branch.branchId}>
                      <TableCell>{branch.branchName || branch.branchId}</TableCell>
                      <TableCell align='right'>{branch.total}</TableCell>
                      {groupColumns.map(group => (
                        <TableCell key={group.key} align='right'>
                          {branch.groups?.[group.key] || 0}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {(stats?.byBranch || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2 + groupColumns.length} align='center'>
                        Keine Daten im gewählten Zeitraum.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default FreeDrinksStatistics;
